import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isNotEmptyAny, refactorSelection } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { globalOpeningAreaAlias } from '@/common/utils/ways';
import { getStandardTaskSelection } from '@/common/api/expandStore/expansiontask';
import { getFranchiseeDetail } from '@/common/api/expandStore/franchisee';
import { AssignType, BusinessMode, franchiseeShortTitleForMode, FranchiseeTypeNameJoint } from '@/common/components/business/ExpandStore/ts-config';
import UserInfoContext from '@/layout/context';
import { useTenantCheckInfo } from '@/common/hook/useTenantCheckInfo';
import { Cascader, Col, Form, Row } from 'antd';
import CirclebusinessMap from '@/common/components/business/ExpandStore/CircleBusinessMap';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Form from '@/common/components/Form/V2Form';
import V2FormDatePicker from '@/common/components/Form/V2FormDatePicker/V2FormDatePicker';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormProvinceList from '@/common/components/Form/V2FormProvinceList';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import FormBusinessMode from '../../../components/FormBusinessMode';
import FormFranchisee from '@/common/components/business/ExpandStore/TaskCreateDrawer/components/FormFranchisee';
import FormFollowingStatus from '../../../components/FormFollowingStatus';
import AssignResponsibleAccount from 'src/views/expandstore/pages/expansioncirclesenmatask/components/Edit/AssignResponsibleAccount';

interface EditMainProps {
  style?: any;
  className?: any;
  isBusiness?: boolean;
  form?: any;
  businessInfo?: any;
  franchiseeDetail?: any;
  isFromFranchisee?: boolean;
  franchiseeAlias?: string;
  taskAssignType?: AssignType; // 指派类型
  taskAssignRoleIds?: any; // 指派类型为指派主管时，可指派对象的角色
  expansionTaskAlias?: string; // 拓店任务别名
}

/**
 * 拓店任务-新增/编辑-主体
 * @description 加盟+直营A（基诺浦）、加盟+直营B（一鸣）
 */
const EditMain: FC<EditMainProps> = ({
  style,
  className,
  isBusiness,
  form,
  businessInfo,
  franchiseeDetail,
  isFromFranchisee,
  franchiseeAlias,
  expansionTaskAlias
}) => {

  const {
    // /** 当前租户的拓店任务模板是否“加盟+直营A（基诺浦）”，未确定时为null */
    // isStandardAndClusterATaskTemplate,
    /** 当前租户的拓店任务模板是否“加盟+直营B（一鸣）”，未确定时为null */
    isStandardAndClusterBTaskTemplate,
  } = useTenantCheckInfo();

  const circlebusinessMap: any = useRef();

  const [selection, setSelection] = useState<{
    /** 紧急程度 */
    emergencyOptions: any[],
  }>({ emergencyOptions: [] });

  const [uniqueName, setUniqueName] = useState<string>(`${franchiseeAlias}唯一标识`); // 加盟商唯一字段名称
  const [franchiseeInited, setFranchiseeInited] = useState<boolean>(false); // 默认加盟商信息时，是否已完成初始化赋值
  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息

  const openingAreaTaskAlias = useMemo(() => {
    return globalOpeningAreaAlias(globalAliasItemVOs);
  }, [globalAliasItemVOs]);

  const targetAddressInfo = Form.useWatch('targetAddressInfo', form);
  const businessName = Form.useWatch('businessName', form);
  const businessMode = Form.useWatch('businessMode', form);
  const followingStatus = Form.useWatch('followingStatus', form);
  const standardFranchiseeId = Form.useWatch('standardFranchiseeId', form);

  const uniqueDefaultNameForMode = (businessMode) => {
    const isJoint = businessMode === BusinessMode.Joint;
    return isJoint ? '联营商唯一标识' : `${franchiseeAlias}唯一标识`;
  };

  useEffect(() => {
    methods.init();
  }, []);

  useEffect(() => {
    // const targetCity = targetAddressInfo && isArray(targetAddressInfo) ? `${targetAddressInfo.slice(0, 2).join('/')}_` : '';
    const targetCity = targetAddressInfo && isNotEmptyAny(targetAddressInfo[1]) ? `${targetAddressInfo[1]}_` : '';
    const targetBusinessName = businessName ? `${businessName}_` : '';

    const taskName = (targetCity || targetBusinessName) ? `${targetCity}${targetBusinessName}${expansionTaskAlias || '拓店任务'}` : '';
    form.setFieldsValue({
      name: taskName
    });
  }, [targetAddressInfo, businessName]);

  useEffect(() => {
    // 从加盟商列表创建任务，并且拿到加盟商详情后，作为默认值
    if (!isFromFranchisee || !isNotEmptyAny(franchiseeDetail)) return;
    const { uniqueName: uniName, franchiseeTypeName } = franchiseeDetail;

    const defaultMode = franchiseeTypeName === FranchiseeTypeNameJoint ? BusinessMode.Joint : BusinessMode.Franchisee; // 按照类型来默认业务模式
    // 初始化回显加盟商信息step1，先设置模式
    form.setFieldsValue({
      businessMode: defaultMode,
    });
    setUniqueName(uniName || uniqueDefaultNameForMode(defaultMode));
  }, [franchiseeDetail]);

  useEffect(() => {
    // 合作模式变动时，修改标识符默认名称
    setUniqueName(uniqueDefaultNameForMode(businessMode));

    // 来自加盟商页的默认模式，还未初始化，不清空
    if (isFromFranchisee && !franchiseeInited && businessMode) {

      // 初始化回显加盟商信息step2，模式确定后才能动态决定组件，此时设置加盟商信息
      const { id, uniqueId } = franchiseeDetail;
      form.setFieldsValue({
        standardFranchiseeId: id,
        uniqueId,
      });

      setFranchiseeInited(true);
      return;
    }
    // 属于后续变动，清空之前的加盟商
    form.setFieldsValue({
      standardFranchiseeId: null,
      uniqueId: null
    });
  }, [businessMode]);

  /**
   * @description 根据合作模式，返回字段标题
   * @return
   */
  const franchiseeTitle = useMemo(() => {
    const isJoint = businessMode === BusinessMode.Joint;
    return isJoint ? '联营商姓名' : `${franchiseeAlias}姓名`;
  }, [businessMode]);

  /**
   * @description 根据合作模式，返回字段简略标题
   * @return
   */
  const franchiseeShortTitle = useMemo(() => {
    return franchiseeShortTitleForMode(businessMode);
  }, [businessMode]);

  const methods = useMethods({

    init() {
      methods.getSelection();
      if (isBusiness) {
        const { id, areaName, provinceId, cityId, districtId, provinceName, cityName, districtName } = businessInfo;
        form.setFieldsValue({
          businessName: areaName,
          targetAddress: [provinceId, cityId, districtId],
          targetAddressInfo: [provinceName, cityName, districtName],
          circleBusinessId: id
        });
      }
    },

    /**
     * 获取下拉选项
     */
    getSelection: () => {
      getStandardTaskSelection().then(res => {
        setSelection(val => ({ ...val,
          emergencyOptions: refactorSelection(res.emergencyDegree),
        }));
      });
    },

    /** 加盟商创建后，回显加盟商信息 */
    handleFranchiseeCreated: (val: any) => {
      form.setFieldValue('standardFranchiseeId', val);

      // 新增加盟商后获取加盟商详情
      methods.getFranchiseeInfo(val);
    },

    /** 选择加盟商 */
    handleFranchisee: (val: any) => {
      form.setFieldValue('standardFranchiseeId', val);

      // 选择加盟商后获取加盟商详情
      methods.getFranchiseeInfo(val);
    },

    /**
     * @description 加盟商详情
     * @param franId
     * @return
     */
    getFranchiseeInfo: (franId: number) => {
      if (franId) {
        getFranchiseeDetail({ id: franId }).then((data) => {
          const { uniqueId, uniqueName: uniName } = data;
          form.setFieldsValue({
            uniqueId: uniqueId,
          });
          setUniqueName(uniName || uniqueDefaultNameForMode(businessMode));
        });
      } else {
        form.setFieldsValue({
          uniqueId: undefined,
        });
      }
    },

  });

  return (
    <>
      <V2Form style={{ ...style }} className={className} form={form}>
        <V2Title type='H2' divider text={`${expansionTaskAlias || '拓店任务'}信息`} className={'mb-16'} />
        <Row gutter={24}>
          <Col span={24} className='mb-16'>
            <CirclebusinessMap
              form ={form}
              ref={circlebusinessMap}
              businessInfo={businessInfo}
              isBusiness={isBusiness}
            />
          </Col>
          <Col span={12}>
            <V2FormInput
              required
              label={openingAreaTaskAlias}
              name='businessName'
              disabled={true}
              placeholder={`请在地图上选择${openingAreaTaskAlias}`}
            />
          </Col>
          <Col span={12}>
            <Form.Item name='targetAddressInfo' hidden noStyle/>
            <V2FormProvinceList
              required
              label='目标城市'
              name='targetAddress'
              disabled={true}
              config={{
                value: [1],
                changeOnSelect: true,
                allowClear: true,
                expandTrigger: 'hover',
                showCheckedStrategy: Cascader.SHOW_PARENT,
                onChange: (_, selectedOptions) => {
                  circlebusinessMap.current?.onChangeProvince && circlebusinessMap.current.onChangeProvince(_, selectedOptions);
                },
                onDropdownVisibleChange: (value) => {
                  circlebusinessMap.current?.onChangeProvince && circlebusinessMap.current.onDropdownVisibleChange(value);

                }
              }}
            />
          </Col>
          <Col span={12}>
            <V2FormInput
              required
              label={`${expansionTaskAlias || '任务'}名称`}
              name='name'
              maxLength={50}
            />
          </Col>
          <Col span={12}>
            <FormFollowingStatus
              required={!isStandardAndClusterBTaskTemplate}
              disabled={[4, 5].includes(followingStatus)} // 如果是已确定、已开业不能修改
              label='跟进状态'
              placeholder='请选择跟进状态'
              name='followingStatus'
              isEdit/>
          </Col>
          <Col span={12}>
            <FormBusinessMode
              label='业务模式'
              placeholder='请选择业务模式'
              name='businessMode'/>
          </Col>
          {/* 如果选择加盟模式，则出现加盟商 */}
          {[BusinessMode.Franchisee, BusinessMode.Joint].includes(businessMode)
            ? <>
              <Col span={12}>
                {/* 根据加盟商模式获取用户列表，所以增加 key */}
                <FormFranchisee
                  key={businessMode}
                  label={franchiseeTitle}
                  name='standardFranchiseeId'
                  placeholder={`请输入${franchiseeTitle}`}
                  createBtnTitle={`去创建${franchiseeShortTitle}`}
                  onCreated={methods.handleFranchiseeCreated}
                  onChange={methods.handleFranchisee}
                  allowClear
                  extraParams={{ businessMode }}
                />
              </Col>
              {standardFranchiseeId && <Col span={12}>
                <V2FormInput
                  label={uniqueName}
                  name='uniqueId'
                  disabled
                />
              </Col>}
            </>
            : ''}
          <Col span={12}>
            <V2FormSelect label='紧急度' name='emergencyDegree' options={selection.emergencyOptions} />
          </Col>
          <Col span={12}>
            {isStandardAndClusterBTaskTemplate
              ? <V2FormDatePicker label='期望商圈复核完成时间' name='expectDropInDate' required />
              : <V2FormDatePicker label='期望落位日期' name='expectDropInDate' />}
          </Col>
        </Row>
        <V2Title type='H2' divider text={`选择${expansionTaskAlias || '拓店'}责任人`} style={{ margin: '16px 0' }} />
        {/* 选择拓店责任人 */}
        <AssignResponsibleAccount form={form}/>
      </V2Form>
    </>
  );
};

export default EditMain;
